import { useQuery } from '@apollo/client';
import { Box, IconButton, Typography } from '@mui/material';
import { graphql } from 'gql';
import { updateDoc } from 'firebase/firestore';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { DebouncedFirebaseTextField } from '../../DebouncedFirebaseTextField';
import DeleteIcon from '../../Icons/DeleteIcon';
import SelectIconWithHover from '../../Icons/SelectIconWithHover';
import type { CompData } from '../../RecordingSessionDataGrid/types';
import { SelectType } from 'gql/graphql';
import { sortBySubTake, sortTakeGroupsByOrdinal } from '../../RecordingSessionDataGrid/util';
import {
    addComp,
    addCompTake,
    removeComp,
    removeCompTake,
    reorderCompLines,
    removeCompLine,
} from '../../RecordingSessionEditor/redux/compThunks';
import {
    selectCompById,
    selectTakeGroupsForLine,
    selectIsDraftSession,
} from '../../RecordingSessionEditor/redux/selectors';
import {
    formatCompText,
    getColorMap,
} from '../../RecordingSessionEditor/util';
import TakeChip from './TakeChip';
import CompText from '../../Comps/CompText';
import DraggableLineItem from './DraggableLineItem';

const GetLineText = graphql(`
    query GetLineText_Comps($lineWhere: LineWhere) {
        lines(where: $lineWhere) {
            id
            lineNo
            text
        }
    }
`)

const GetAudioText = graphql(`
    query GetAudioText_Comps($audioWhere: AudioWhere) {
        audio(where: $audioWhere) {
            id
            tag
            text
        }
    }
`)

type Props = {
    sessionId: string
    episodeId: string
    /** @deprecated Use lineIds instead */
    lineId?: string
    /** Multiple line IDs (same revision chain). Supersedes lineId. */
    lineIds?: string[]
    compId?: string
    onCompCreated?: (compId: string) => void
    onLineIdsChange?: (lineIds: string[]) => void
    showLineNo?: boolean
    /** When provided, skips the GetLineText query and uses this text directly (e.g. for revision lines) */
    lineText?: string
    /** Map of lineId → text for multi-line comps (avoids N+1 GraphQL queries) */
    lineTexts?: Record<string, string>
    /** Map of lineId → display label (e.g. "001 - CharName (Revision 2)") */
    lineLabels?: Record<string, string>
}

export default function Comp({
    sessionId, episodeId, lineId, lineIds, compId, onCompCreated, onLineIdsChange,
    showLineNo = true, lineText: lineTextProp, lineTexts: lineTextsProp, lineLabels: lineLabelsProp,
}: Props) {
    const dispatch = useAppDispatch();

    // Normalize to array: use lineIds if provided, fall back to lineId.
    // Trust the incoming order (Revision N → ... → Revision 1 → Original) — it's
    // already established upstream (handleLinesChange / sortLineIdsForComp) and is
    // the single source of truth, regardless of the order lines were selected in.
    const allLineIds: string[] = useMemo(() => {
        if (lineIds && lineIds.length > 0) {
            return lineIds;
        }
        if (lineId) return [lineId];
        return [];
    }, [lineIds, lineId]);

    // Collect take groups for all line IDs
    const allTakeGroups = useAppSelector((state) => {
        const tgSet = new Map<string, typeof state.recordingSession.takeGroups[number]>();
        for (const lid of allLineIds) {
            const tgs = selectTakeGroupsForLine(state, lid);
            for (const tg of tgs) {
                if (!tgSet.has(tg.id!)) tgSet.set(tg.id!, tg);
            }
        }
        return [...tgSet.values()];
    });

    const [currentCompId, setCurrentCompId] = useState<string>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (compId) setCurrentCompId(compId);
    }, [compId]);

    const comp = useAppSelector((state) => selectCompById(state, currentCompId ?? ''));
    const isDraftSession = useAppSelector(selectIsDraftSession);

    // GraphQL queries for line text — only for single-line backward compat
    const primaryLineId = allLineIds[0] ?? '';
    const { data: lineData, loading: loadingLineData } = useQuery(GetLineText, {
        variables: {
            lineWhere: {
                id: primaryLineId ? primaryLineId.replace('LINE-', '') : '',
            },
        },
        skip: !primaryLineId || primaryLineId.includes('AUDIO') || lineTextProp !== undefined || allLineIds.length > 1,
    })

    const { data: audioData, loading: loadingAudioData } = useQuery(GetAudioText, {
        variables: {
            audioWhere: {
                id: primaryLineId ? primaryLineId.replace('AUDIO-', '') : '',
            },
        },
        skip: !primaryLineId || !primaryLineId.includes('AUDIO') || allLineIds.length > 1,
    });

    const handleClick = (forLineId: string, takeId: string, lineIndex?: number) => {
        const existingComp = comp?.takes.find(t => t.takeId === takeId);

        if (currentCompId && existingComp) {
            setLoading(true);
            dispatch(removeCompTake({ sessionId, episodeId, takeId, compId: currentCompId }))
                .unwrap()
                .then(() => setLoading(false));
            return;
        }

        const selected = window.getSelection()
        const selectedText = selected?.toString();

        if (!selected || !selectedText) return;

        // Match against the specific line's text
        const rawText = lineTexts[forLineId] ?? lineTextProp ?? lineData?.lines[0]?.text ?? audioData?.audio[0]?.text;
        if (!rawText) return;

        const formattedText = formatCompText(rawText);
        const formattedSelected = formatCompText(selectedText);
        if (!formattedText.includes(formattedSelected)) return;

        const prevSibling = selected.anchorNode?.previousSibling;
        // @ts-ignore
        const lastPos = parseInt(prevSibling?.dataset?.endpos ?? '0');

        const subString = {
            begin: Math.min(selected.anchorOffset, selected.focusOffset) + lastPos,
            end: Math.max( selected.anchorOffset, selected.focusOffset) + lastPos,
        }

        setLoading(true);
        if (!currentCompId) {
            // Create comp with all selected lineIds, not just the current one
            dispatch(addComp({ sessionId, episodeId, lineIds: allLineIds, takeId, subString, lineId: forLineId, lineIndex }))
                .unwrap()
                .then((id) => {
                    onCompCreated?.(id);
                    setCurrentCompId(id);
                    setLoading(false);
                });
        } else {
            // Always append lineId to end (allows duplicates)
            // Pass allLineIds so the thunk can keep comp.lineIds in sync with any new position
            dispatch(
                addCompTake({
                    sessionId, episodeId, compId: currentCompId, takeId, subString, lineId: forLineId, lineIndex, lineIds: allLineIds,
                }))
                .unwrap()
                .then(() => setLoading(false));
        }
    }

    const handleDeleteComp = () => {
        if (!currentCompId) return;

        setLoading(true);
        dispatch(removeComp({ sessionId, episodeId, compId: currentCompId }))
            .unwrap()
            .then(() => {
                setCurrentCompId(undefined);
                setLoading(false);
            });
    }

    const handleDeleteLine = (index: number) => {
        const newLineIds = lineIdsToRender.filter((_, i) => i !== index);

        // If comp exists in Redux, remove from there
        if (comp) {
            setLoading(true);
            dispatch(removeCompLine({
                compId: comp.id!,
                lineIndex: index,
                sessionId,
                episodeId,
            }))
                .unwrap()
                .then(() => {
                    setLoading(false);
                    onLineIdsChange?.(newLineIds);
                });
        } else {
            // If comp doesn't exist yet, just update parent state
            onLineIdsChange?.(newLineIds);
        }
    };

    const handleAltClick = (compId?: string) => {
        if (!compId || !comp) return;

        const newSelectType = comp.selectType === SelectType.Alt ? SelectType.None : SelectType.Alt;
        updateDoc(comp.ref!, { selectType: newSelectType });
    };

    const handleReorderLine = (fromIndex: number, toIndex: number) => {
        const newLineIds = [...lineIdsToRender];
        const [removed] = newLineIds.splice(fromIndex, 1);
        newLineIds.splice(toIndex, 0, removed);

        // If comp exists, update it in Redux
        const compId = comp?.id || currentCompId;
        if (compId) {
            dispatch(reorderCompLines({
                compId,
                fromIndex,
                toIndex,
                sessionId,
                episodeId,
            }));
        }

        // Always update parent state (works even if comp not created yet)
        onLineIdsChange?.(newLineIds);
    };

    const colorMap = getColorMap(comp);

    // Group take groups by line ID for display
    const takeGroupsByLine = useMemo(() => {
        const map = new Map<string, typeof allTakeGroups>();
        for (const lid of allLineIds) {
            const groups = allTakeGroups.filter(tg => tg.lineIds.includes(lid));
            if (groups.length > 0) map.set(lid, groups);
        }
        return map;
    }, [allTakeGroups, allLineIds]);

    // Build line text map from props or query results
    const lineTexts = useMemo(() => {
        if (lineTextsProp && Object.keys(lineTextsProp).length > 0) return lineTextsProp;
        if (lineTextProp) return { [primaryLineId]: lineTextProp };
        const texts: Record<string, string> = {};
        if (lineData?.lines[0]) texts[primaryLineId] = lineData.lines[0].text;
        if (audioData?.audio[0]) texts[primaryLineId] = audioData.audio[0].text;
        return texts;
    }, [lineTextsProp, lineTextProp, lineData, audioData, primaryLineId]);

    const sortedData = allTakeGroups.toSorted(sortTakeGroupsByOrdinal).map(g => ({
        ...g,
        subTake: g.subTakes.toSorted(sortBySubTake),
    }));

    // For single line: render as before. For multiple lines: group by line.
    const renderTakesForLine = (lineId: string, groups: typeof allTakeGroups, lineIndex?: number) => {
        const lineGroups = groups.length > 0
            ? groups.toSorted(sortTakeGroupsByOrdinal).map(g => ({
                ...g,
                subTake: g.subTakes.toSorted(sortBySubTake),
            }))
            : sortedData.filter(g => g.lineIds.includes(lineId));

        const lineText = lineTexts[lineId] ?? lineTextProp ?? lineData?.lines[0]?.text ?? audioData?.audio[0]?.text ?? '';
        const lineLabel = lineLabelsProp?.[lineId] ?? '';

        const lineComp = comp ? {
            ...comp,
            takes: comp.takes.filter(t => {
                if (t.lineId) {
                    if (t.lineId !== lineId) return false;
                    if (lineIndex === undefined) return true;
                    const takeLineIndex = (t as { lineIndex?: number }).lineIndex;
                    if (takeLineIndex !== undefined) return takeLineIndex === lineIndex;
                    return lineIndex === lineIdsToRender.indexOf(lineId);
                }
                return lineIndex === undefined || lineIndex === 0;
            }),
        } : undefined;

        const handleTakeClick = (takeId: string) => handleClick(lineId, takeId, lineIndex);

        return (
            <Box key={lineId} sx={{ mb: 1 }}>
                {lineLabel && (
                    <Typography variant='caption' color='text.secondary' sx={{ ml: 1 }}>
                        {lineLabel}
                    </Typography>
                )}
                <Box sx={{ m: 1, overflowWrap: 'anywhere' }}>
                    <CompText
                        lineNo={showLineNo ? (lineData?.lines[0]?.lineNo ?? audioData?.audio[0]?.tag ?? '') : undefined}
                        text={lineText}
                        loading={!lineText && allLineIds.length <= 1 && (loadingLineData || loadingAudioData)}
                        comp={lineComp}
                        colorMap={colorMap}
                    />
                </Box>
                {lineGroups.map((group) => (
                    <Box key={group.id} sx={{ m: 1 }}>
                        Take {group.take}:
                        {group.subTake.map((take) => (
                            <TakeChip
                                key={take.id!}
                                data={take}
                                loading={loading}
                                onClick={handleTakeClick}
                                color={colorMap ? colorMap[take.id!] : undefined}
                            />
                        ))}
                    </Box>
                ))}
            </Box>
        );
    };

    // Use allLineIds for rendering (always use props to stay in sync with parent)
    const lineIdsToRender = allLineIds.length > 0 ? allLineIds : comp?.lineIds ?? [];

    return (
        <>
            {lineIdsToRender.length > 1
                ? lineIdsToRender.map((lineId, index) => (
                    <DraggableLineItem
                        key={`${lineId}-${index}`}
                        index={index}
                        position={index + 1}
                        onDelete={() => handleDeleteLine(index)}
                        onReorder={handleReorderLine}
                        disabled={loading}
                    >
                        {renderTakesForLine(lineId, takeGroupsByLine.get(lineId) ?? [], index)}
                    </DraggableLineItem>
                ))
                : renderTakesForLine(
                    lineIdsToRender[0] ?? primaryLineId,
                    lineIdsToRender[0] ? (takeGroupsByLine.get(lineIdsToRender[0]) ?? []) : allTakeGroups
                )
            }
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
            }}>
                {currentCompId && isDraftSession && (
                    <SelectIconWithHover
                        selectType={comp?.selectType ?? SelectType.None}
                        onCircleClick={() => {}}
                        onAltClick={() => handleAltClick(currentCompId)}
                        disabledCircle={true}
                        disabledCircleTooltip="Finish Session to select this comp"
                    />
                )}

                {comp && <DebouncedFirebaseTextField<CompData> objRef={comp.ref!} tooltip='Note' valKey='note' obj={comp} /> }
                {comp && (
                    <IconButton
                        aria-label='delete'
                        onClick={handleDeleteComp}
                        size='small'
                    >
                        <DeleteIcon />
                    </IconButton>
                )}
            </Box>
        </>
    )
}